import { useMemo } from 'react';
import { wordDiff, htmlToReadableText, type DiffPart } from '../../lib/diff';

interface DiffViewProps {
  before: string; // HTML
  after: string; // HTML
  darkMode: boolean;
}

/**
 * Vorher/Nachher-Vergleich eines Blocks, nebeneinander, mit wortweiser
 * Hervorhebung der Änderungen. Der Diff arbeitet auf der Klartext-Projektion
 * (gut lesbar); übernommen wird später das vollständige HTML (afterText).
 */
export default function DiffView({ before, after, darkMode }: DiffViewProps) {
  const beforeText = useMemo(() => htmlToReadableText(before), [before]);
  const afterText = useMemo(() => htmlToReadableText(after), [after]);
  const parts = useMemo(() => wordDiff(beforeText, afterText), [beforeText, afterText]);

  const delCls = darkMode
    ? 'bg-red-900/40 text-red-300 line-through decoration-red-500/60'
    : 'bg-red-100 text-red-700 line-through decoration-red-400';
  const insCls = darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';

  const renderSide = (keep: DiffPart['type']) => (
    <div className="text-[15px] leading-relaxed whitespace-pre-wrap break-words">
      {parts.map((p, i) => {
        if (p.type === 'eq') return <span key={i}>{p.text}</span>;
        if (p.type === 'del' && keep === 'del') return <span key={i} className={`rounded-sm ${delCls}`}>{p.text}</span>;
        if (p.type === 'ins' && keep === 'ins') return <span key={i} className={`rounded-sm ${insCls}`}>{p.text}</span>;
        return null;
      })}
    </div>
  );

  const paneBase = darkMode ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-slate-200';
  const headBase = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className={`rounded-lg border p-3 ${paneBase}`}>
        <div className={`text-[13px] font-semibold uppercase tracking-wide mb-1.5 ${headBase}`}>Vorher</div>
        {renderSide('del')}
      </div>
      <div className={`rounded-lg border p-3 ${darkMode ? 'bg-green-950/20 border-green-800/50' : 'bg-green-50/50 border-green-200'}`}>
        <div className={`text-[13px] font-semibold uppercase tracking-wide mb-1.5 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Nachher (Vorschlag)</div>
        {renderSide('ins')}
      </div>
    </div>
  );
}
